/**
 * Premium German Theme - Main JavaScript
 * Modern, vanilla JS implementation
 */

class ThemeManager {
  constructor() {
    this.init();
  }

  init() {
    this.setupMobileMenu();
    this.setupCartDrawer();
    this.setupQuantityInputs();
    this.setupProductVariants();
    this.setupScrollEffects();
    this.setupImageZoom();
    this.setupAccordions();
    this.setupTabs();
  }

  // Mobile Menu Toggle
  setupMobileMenu() {
    const menuToggle = document.querySelector('[data-menu-toggle]');
    const mobileMenu = document.querySelector('[data-mobile-menu]');
    const menuClose = document.querySelector('[data-menu-close]');

    if (menuToggle && mobileMenu) {
      menuToggle.addEventListener('click', () => {
        mobileMenu.classList.toggle('is-open');
        document.body.classList.toggle('menu-open');
      });

      if (menuClose) {
        menuClose.addEventListener('click', () => {
          mobileMenu.classList.remove('is-open');
          document.body.classList.remove('menu-open');
        });
      }
    }
  }

  // Cart Drawer
  setupCartDrawer() {
    const cartToggle = document.querySelectorAll('[data-cart-toggle]');
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    const cartClose = document.querySelector('[data-cart-close]');

    cartToggle.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        if (cartDrawer) {
          cartDrawer.classList.toggle('is-open');
          document.body.classList.toggle('cart-open');
        }
      });
    });

    if (cartClose && cartDrawer) {
      cartClose.addEventListener('click', () => {
        cartDrawer.classList.remove('is-open');
        document.body.classList.remove('cart-open');
      });
    }
  }

  // Quantity Inputs
  setupQuantityInputs() {
    const quantityInputs = document.querySelectorAll('[data-quantity-input]');

    quantityInputs.forEach(input => {
      const decreaseBtn = input.querySelector('[data-quantity-decrease]');
      const increaseBtn = input.querySelector('[data-quantity-increase]');
      const valueInput = input.querySelector('input[type="number"]');

      if (decreaseBtn && valueInput) {
        decreaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(valueInput.value) || 1;
          const minValue = parseInt(valueInput.min) || 1;
          if (currentValue > minValue) {
            valueInput.value = currentValue - 1;
            valueInput.dispatchEvent(new Event('change'));
          }
        });
      }

      if (increaseBtn && valueInput) {
        increaseBtn.addEventListener('click', () => {
          const currentValue = parseInt(valueInput.value) || 1;
          const maxValue = parseInt(valueInput.max) || Infinity;
          if (currentValue < maxValue) {
            valueInput.value = currentValue + 1;
            valueInput.dispatchEvent(new Event('change'));
          }
        });
      }
    });
  }

  // Product Variants
  setupProductVariants() {
    const variantSelectors = document.querySelectorAll('[data-variant-selector]');

    variantSelectors.forEach(selector => {
      selector.addEventListener('change', (e) => {
        const selectedVariant = e.target.value;
        this.updateProductVariant(selectedVariant);
      });
    });
  }

  updateProductVariant(variantId) {
    // Update price, availability, images based on variant
    // This is a placeholder for variant logic
    console.log('Variant updated:', variantId);
  }

  // Scroll Effects
  setupScrollEffects() {
    const header = document.querySelector('[data-header]');
    let lastScroll = 0;

    if (header) {
      window.addEventListener('scroll', () => {
        const currentScroll = window.pageYOffset;

        if (currentScroll > 100) {
          header.classList.add('is-scrolled');
        } else {
          header.classList.remove('is-scrolled');
        }

        // Hide header on scroll down, show on scroll up
        if (currentScroll > lastScroll && currentScroll > 200) {
          header.classList.add('is-hidden');
        } else {
          header.classList.remove('is-hidden');
        }

        lastScroll = currentScroll;
      });
    }

    // Fade in elements on scroll
    const fadeElements = document.querySelectorAll('[data-fade-in]');
    
    if (fadeElements.length > 0 && 'IntersectionObserver' in window) {
      const fadeObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            fadeObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      fadeElements.forEach(element => {
        fadeObserver.observe(element);
      });
    }
  }

  // Image Zoom on Product Page
  setupImageZoom() {
    const zoomImages = document.querySelectorAll('[data-zoom-image]');

    zoomImages.forEach(image => {
      image.addEventListener('mouseenter', () => {
        image.style.cursor = 'zoom-in';
      });

      image.addEventListener('click', () => {
        this.openImageModal(image.src);
      });
    });
  }

  openImageModal(imageSrc) {
    // Create and open image modal
    // Placeholder for modal logic
    console.log('Open image modal:', imageSrc);
  }

  // Accordions
  setupAccordions() {
    const accordions = document.querySelectorAll('[data-accordion]');

    accordions.forEach(accordion => {
      const trigger = accordion.querySelector('[data-accordion-trigger]');
      const content = accordion.querySelector('[data-accordion-content]');

      if (trigger && content) {
        trigger.addEventListener('click', () => {
          const isOpen = accordion.classList.contains('is-open');
          
          // Close all accordions in same group
          const group = accordion.closest('[data-accordion-group]');
          if (group) {
            group.querySelectorAll('[data-accordion]').forEach(item => {
              item.classList.remove('is-open');
            });
          }

          // Toggle current accordion
          if (!isOpen) {
            accordion.classList.add('is-open');
          }
        });
      }
    });
  }

  // Tabs
  setupTabs() {
    const tabGroups = document.querySelectorAll('[data-tabs]');

    tabGroups.forEach(group => {
      const triggers = group.querySelectorAll('[data-tab-trigger]');
      const contents = group.querySelectorAll('[data-tab-content]');

      triggers.forEach(trigger => {
        trigger.addEventListener('click', () => {
          const targetId = trigger.getAttribute('data-tab-trigger');

          // Remove active class from all
          triggers.forEach(t => t.classList.remove('is-active'));
          contents.forEach(c => c.classList.remove('is-active'));

          // Add active class to current
          trigger.classList.add('is-active');
          const targetContent = group.querySelector(`[data-tab-content="${targetId}"]`);
          if (targetContent) {
            targetContent.classList.add('is-active');
          }
        });
      });
    });
  }
}

// Initialize theme when DOM is ready
if (document.readyState !== 'loading') {
  new ThemeManager();
} else {
  document.addEventListener('DOMContentLoaded', () => {
    new ThemeManager();
  });
}

// Utility Functions
const debounce = (func, delay = 250) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
};

const throttle = (func, delay = 250) => {
  let lastCall = 0;
  return (...args) => {
    const now = new Date().getTime();
    if (now - lastCall < delay) return;
    lastCall = now;
    return func(...args);
  };
};

// Export for potential use in other scripts
window.ThemeUtils = {
  debounce,
  throttle
};
